/** Tokens visuais — WhatsApp Cloud API Klinvia (frontend-design + agendaTheme) */
import { AGENDA_THEME } from './agendaTheme';

export const CLOUD_API_THEME = {
  ...AGENDA_THEME,
  whatsappGreen: '#128C7E',
  whatsappGreenHover: '#0F766E',
  whatsappGreenSoft: 'rgba(18, 140, 126, 0.08)',
  whatsappGreenBorder: 'rgba(37, 211, 102, 0.5)',
  borderStrong: 'rgba(13, 148, 136, 0.28)',
  borderHover: 'rgba(13, 148, 136, 0.35)',
  borderHoverStrong: 'rgba(13, 148, 136, 0.5)',
  tealFocus: 'rgba(13, 148, 136, 0.55)',
  tealFocusText: 'rgba(15, 118, 110, 0.92)',
  primaryGradient: `linear-gradient(135deg, ${AGENDA_THEME.teal} 0%, ${AGENDA_THEME.tealDark} 100%)`,
  primaryGradientHover: `linear-gradient(135deg, ${AGENDA_THEME.tealBright} 0%, ${AGENDA_THEME.teal} 100%)`,
  tableHeadBg: AGENDA_THEME.tealSoft,
  tableHeadBorder: AGENDA_THEME.border,
  tableRowHover: AGENDA_THEME.gridHover,
  tableRowBorder: 'rgba(13, 148, 136, 0.08)',
} as const;

export const cloudApiClinicalGridBg = {
  backgroundColor: CLOUD_API_THEME.muted,
  backgroundImage: `
    linear-gradient(${CLOUD_API_THEME.gridLine} 1px, transparent 1px),
    linear-gradient(90deg, ${CLOUD_API_THEME.gridLine} 1px, transparent 1px)
  `,
  backgroundSize: '24px 24px',
};

export const cloudApiPageShellSx = {
  ...cloudApiClinicalGridBg,
  p: { xs: 2, md: 3 },
  minHeight: '100%',
  flex: 1,
  color: CLOUD_API_THEME.navy,
};

export const cloudApiHeaderSx = {
  mb: { xs: 2, md: 3 },
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: { xs: 'flex-start', md: 'center' },
  flexDirection: { xs: 'column', md: 'row' },
  gap: { xs: 2, md: 0 },
  p: { xs: 2, md: '20px 24px' },
  borderRadius: 2,
  border: `1px solid ${CLOUD_API_THEME.borderSoft}`,
  bgcolor: CLOUD_API_THEME.paper,
  boxShadow: CLOUD_API_THEME.shadow,
};

export const cloudApiTitleSx = {
  fontWeight: 700,
  color: CLOUD_API_THEME.navy,
  fontSize: { xs: '1.5rem', md: '2rem' },
  letterSpacing: '-0.02em',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

export const cloudApiHeaderIconSx = {
  fontSize: { xs: 24, md: 32 },
  color: CLOUD_API_THEME.teal,
};

export const cloudApiSubtitleSx = {
  color: CLOUD_API_THEME.slate,
  fontSize: { xs: '0.75rem', md: '0.875rem' },
};

export const cloudApiTabsWrapperSx = {
  borderBottom: `1px solid ${CLOUD_API_THEME.borderSoft}`,
  mb: 2,
  bgcolor: CLOUD_API_THEME.paper,
  borderRadius: '12px 12px 0 0',
  px: 1,
};

export const cloudApiTabsSx = {
  minHeight: { xs: 44, sm: 48 },
  '& .MuiTab-root': {
    textTransform: 'none' as const,
    fontWeight: 600,
    color: CLOUD_API_THEME.slate,
  },
  '& .MuiTab-root.Mui-selected': { color: CLOUD_API_THEME.tealDark },
  '& .MuiTabs-indicator': {
    bgcolor: CLOUD_API_THEME.teal,
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
};

export const cloudApiPrimaryButtonSx = {
  bgcolor: CLOUD_API_THEME.teal,
  textTransform: 'none' as const,
  fontWeight: 600,
  boxShadow: '0 2px 8px rgba(13, 148, 136, 0.22)',
  '&:hover': { bgcolor: CLOUD_API_THEME.tealDark },
};

export const cloudApiOutlinedButtonSx = {
  borderColor: CLOUD_API_THEME.border,
  color: CLOUD_API_THEME.navy,
  textTransform: 'none' as const,
  fontWeight: 600,
  '&:hover': {
    borderColor: CLOUD_API_THEME.teal,
    bgcolor: CLOUD_API_THEME.tealSoft,
  },
};

export const cloudApiWhatsappButtonSx = {
  borderColor: CLOUD_API_THEME.whatsappGreenBorder,
  color: CLOUD_API_THEME.whatsappGreen,
  textTransform: 'none' as const,
  fontWeight: 600,
  '&:hover': {
    borderColor: '#25D366',
    bgcolor: CLOUD_API_THEME.whatsappGreenSoft,
  },
};

export const cloudApiCardSx = {
  mb: 3,
  borderRadius: 2,
  border: `1px solid ${CLOUD_API_THEME.borderSoft}`,
  bgcolor: CLOUD_API_THEME.paper,
  boxShadow: CLOUD_API_THEME.shadow,
};

export const cloudApiEmptyStateCardSx = {
  borderRadius: 2,
  border: `1px solid ${CLOUD_API_THEME.borderSoft}`,
  bgcolor: CLOUD_API_THEME.paper,
  boxShadow: CLOUD_API_THEME.shadow,
};

export const cloudApiTableContainerSx = {
  borderRadius: 2,
  border: `1px solid ${CLOUD_API_THEME.borderSoft}`,
  boxShadow: CLOUD_API_THEME.shadow,
  overflow: 'hidden',
  '& .MuiTableHead-root .MuiTableCell-root': {
    color: CLOUD_API_THEME.navy,
    fontWeight: 600,
    borderBottom: `1px solid ${CLOUD_API_THEME.tableHeadBorder}`,
    backgroundColor: CLOUD_API_THEME.tableHeadBg,
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    color: CLOUD_API_THEME.navy,
    borderBottom: `1px solid ${CLOUD_API_THEME.tableRowBorder}`,
  },
  '& .MuiTableBody-root .MuiTableRow-root:hover': {
    backgroundColor: CLOUD_API_THEME.tableRowHover,
  },
};

export const cloudApiCellPrimarySx = {
  color: CLOUD_API_THEME.navy,
};

export const cloudApiCellSecondarySx = {
  color: CLOUD_API_THEME.slateMid,
};

export const cloudApiInputFieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: CLOUD_API_THEME.tealSoft,
    borderRadius: 2,
    color: CLOUD_API_THEME.navy,
    transition: 'all 0.2s ease',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: CLOUD_API_THEME.borderHover,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: CLOUD_API_THEME.teal,
      boxShadow: `0 0 0 3px ${CLOUD_API_THEME.tealMuted}`,
    },
  },
  '& .MuiInputLabel-root': {
    color: CLOUD_API_THEME.slate,
    '&.Mui-focused': { color: CLOUD_API_THEME.tealDark },
  },
  '& .MuiFormHelperText-root': {
    color: CLOUD_API_THEME.slateFaint,
  },
};

export const cloudApiDialogPaperSx = {
  borderRadius: 3,
  border: `1px solid ${CLOUD_API_THEME.borderSoft}`,
  boxShadow: CLOUD_API_THEME.dialogShadow,
  overflow: 'hidden',
};

export const cloudApiDialogHeaderSx = {
  ...cloudApiClinicalGridBg,
  color: CLOUD_API_THEME.navy,
  borderBottom: `1px solid ${CLOUD_API_THEME.borderSoft}`,
  fontWeight: 700,
  fontSize: '1.15rem',
  py: 2,
  px: 3,
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
};

export const cloudApiDialogHeaderIconSx = {
  fontSize: 28,
  color: CLOUD_API_THEME.teal,
};

export const cloudApiDialogSectionTitleSx = {
  color: CLOUD_API_THEME.tealDark,
  fontWeight: 700,
  mb: 2,
  fontSize: '0.875rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
};

export const cloudApiDialogContentSx = {
  p: 3,
  bgcolor: CLOUD_API_THEME.paper,
};

export const cloudApiDialogFooterSx = {
  px: 3,
  py: 2,
  borderTop: `1px solid ${CLOUD_API_THEME.borderSoft}`,
  bgcolor: CLOUD_API_THEME.muted,
  gap: 1,
};

export const cloudApiDialogCancelButtonSx = {
  color: CLOUD_API_THEME.slate,
  textTransform: 'none' as const,
  fontWeight: 600,
};

export const cloudApiDialogPrimaryButtonSx = {
  background: CLOUD_API_THEME.primaryGradient,
  color: '#fff',
  textTransform: 'none' as const,
  fontWeight: 600,
  boxShadow: '0 4px 12px rgba(13, 148, 136, 0.25)',
  '&:hover': {
    background: CLOUD_API_THEME.primaryGradientHover,
  },
};

export const cloudApiCampaignCardSx = {
  height: '100%',
  borderRadius: 2,
  border: `1px solid ${CLOUD_API_THEME.borderSoft}`,
  boxShadow: CLOUD_API_THEME.shadow,
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    borderColor: CLOUD_API_THEME.border,
    boxShadow: '0 4px 16px rgba(15, 23, 42, 0.08)',
  },
};

export const cloudApiCampaignTypeCardSx = {
  cursor: 'pointer',
  border: `2px solid ${CLOUD_API_THEME.border}`,
  borderRadius: 2,
  transition: 'all 0.2s ease',
  '&:hover': {
    bgcolor: CLOUD_API_THEME.tealSoft,
    borderColor: CLOUD_API_THEME.teal,
  },
};

export const cloudApiLinearProgressSx = {
  height: 8,
  borderRadius: 4,
  bgcolor: CLOUD_API_THEME.mutedAlt,
  '& .MuiLinearProgress-bar': {
    bgcolor: CLOUD_API_THEME.teal,
  },
};

export const cloudApiTemplateToolbarSx = {
  display: 'flex',
  gap: 2,
  flexWrap: 'wrap' as const,
  alignItems: 'center',
  mb: 2,
  p: 2,
  borderRadius: 2,
  border: `1px solid ${CLOUD_API_THEME.borderSoft}`,
  bgcolor: CLOUD_API_THEME.paper,
};

export const cloudApiTemplateCardSx = {
  height: '100%',
  borderRadius: 2,
  border: `1px solid ${CLOUD_API_THEME.borderSoft}`,
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
};

export const cloudApiTemplatePreviewSx = {
  p: 1.5,
  bgcolor: CLOUD_API_THEME.muted,
  whiteSpace: 'pre-wrap' as const,
  mb: 1,
  fontSize: '0.85rem',
  borderRadius: 1.5,
  border: `1px solid ${CLOUD_API_THEME.borderSoft}`,
};

export const cloudApiStatCardSx = {
  ...cloudApiCardSx,
  mb: 0,
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 16px rgba(15, 23, 42, 0.08)',
  },
};

export const cloudApiStatAvatarTealSx = {
  bgcolor: CLOUD_API_THEME.teal,
  boxShadow: '0 4px 12px rgba(13, 148, 136, 0.25)',
};

export const cloudApiStatValueSx = {
  fontWeight: 700,
  color: CLOUD_API_THEME.navy,
};

export const cloudApiTabsPaperSx = {
  mb: 3,
  borderRadius: 2,
  border: `1px solid ${CLOUD_API_THEME.borderSoft}`,
  bgcolor: CLOUD_API_THEME.paper,
  boxShadow: CLOUD_API_THEME.shadow,
  overflow: 'hidden',
};

export const cloudApiInstanceCardSx = {
  ...cloudApiCampaignCardSx,
};

export const cloudApiProviderChipSx = {
  mt: 0.5,
  bgcolor: CLOUD_API_THEME.tealSoft,
  color: CLOUD_API_THEME.navy,
  border: `1px solid ${CLOUD_API_THEME.border}`,
  fontWeight: 600,
};

export const cloudApiQrFrameSx = {
  p: 2,
  border: `2px dashed ${CLOUD_API_THEME.teal}`,
  borderRadius: 2,
  backgroundColor: CLOUD_API_THEME.tealSoft,
};

export const cloudApiLoadingShellSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '60vh',
  ...cloudApiClinicalGridBg,
};

export const cloudApiKpiCardSx = {
  bgcolor: CLOUD_API_THEME.paper,
  border: `1px solid ${CLOUD_API_THEME.borderSoft}`,
  boxShadow: CLOUD_API_THEME.shadow,
  borderRadius: 2,
};

/** Cartão de item em listas mobile (gestão de usuários, etc.). */
export const cloudApiListCardSx = {
  ...cloudApiKpiCardSx,
  transition: 'border-color 0.2s ease, background-color 0.15s ease',
  '&:active': { bgcolor: CLOUD_API_THEME.gridHover },
};

export const cloudApiKpiLabelSx = {
  color: CLOUD_API_THEME.slate,
  fontWeight: 600,
};

export const cloudApiFilterBarSx = {
  mb: 3,
  display: 'flex',
  gap: 2,
  flexWrap: 'wrap' as const,
  alignItems: 'center',
  p: 2,
  borderRadius: 2,
  border: `1px solid ${CLOUD_API_THEME.borderSoft}`,
  bgcolor: CLOUD_API_THEME.paper,
  boxShadow: CLOUD_API_THEME.shadow,
};

export const cloudApiPaginationButtonSx = {
  textTransform: 'none' as const,
  color: CLOUD_API_THEME.teal,
  fontWeight: 600,
  '&:hover': { bgcolor: CLOUD_API_THEME.tealSoft },
  '&.Mui-disabled': { color: CLOUD_API_THEME.slateFaint },
};

export const cloudApiMenuPaperSx = {
  borderRadius: 2,
  boxShadow: CLOUD_API_THEME.dialogShadow,
  border: `1px solid ${CLOUD_API_THEME.borderSoft}`,
};

export const cloudApiTealIconButtonSx = {
  color: CLOUD_API_THEME.teal,
  '&:hover': { bgcolor: CLOUD_API_THEME.tealSoft },
};

export const cloudApiSectionTitleSx = {
  fontWeight: 700,
  mb: 1.5,
  color: CLOUD_API_THEME.tealDark,
};

export const cloudApiSectionTitleCompactSx = {
  fontWeight: 700,
  mb: 1,
  color: CLOUD_API_THEME.tealDark,
};

export const cloudApiToggleGroupSx = {
  '& .Mui-selected': {
    bgcolor: `${CLOUD_API_THEME.teal} !important`,
    color: '#fff !important',
  },
};

export const cloudApiAiOutlinedButtonSx = {
  borderColor: CLOUD_API_THEME.borderStrong,
  color: CLOUD_API_THEME.tealDark,
  textTransform: 'none' as const,
  fontWeight: 600,
  '&:hover': {
    borderColor: CLOUD_API_THEME.teal,
    bgcolor: CLOUD_API_THEME.tealSoft,
  },
};
